// src/pages/registrar-evidencias.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { resolveApiBaseList } from '../utils/apiBase';

Dropzone.autoDiscover = false;

/* ---------- Util: sanitizar texto simple y cortar a 254 ---------- */
const sanitize254 = (s='') =>
  String(s).replace(/[<>]/g, '').trim().slice(0, 254);

const RegistrarEvidencias = () => {
  const dropzoneRef = useRef(null);
  const dzInstance = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [descripcion, setDescripcion] = useState('');
  const [idPlaneacion, setIdPlaneacion] = useState('');
  const [planeacionesDisponibles, setPlaneacionesDisponibles] = useState([]); // R y SIN evidencias
  const [siguienteId, setSiguienteId] = useState('');     // IdEvidencia siguiente (solo lectura)
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  /* ---------- Salir ---------- */
  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) navigate(`/${rol}/inicio`, { replace: true });
    else navigate('/inicio', { replace: true });
  };

  /* ---------- Helper: fetch con fallback y parse JSON seguro ---------- */
  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // deja message como texto crudo
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return { data: {}, baseUrl: normalizedBase };

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  /* ---------- Inicializar Dropzone ---------- */
  useEffect(() => {
    if (dropzoneRef.current && Dropzone.instances.length === 0) {
      dzInstance.current = new Dropzone(dropzoneRef.current, {
        url: '/fake-url', // no se usa, subimos con fetch manual
        autoProcessQueue: false,
        parallelUploads: 500, // Permite subir hasta 500 archivos a la vez
        uploadMultiple: true, // Habilita la carga múltiple real
        maxFilesize: 10, // MB por archivo
        acceptedFiles: '.jpg,.png,.jpeg,.pdf,.docx,.xlsx,.pptx',
        addRemoveLinks: true,
        dictDefaultMessage:
          'Arrastra y suelta tus archivos aquí o haz clic para seleccionarlos.',
        init: function () {
          this.on('addedfile', function (file) {
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (ext === 'pdf')
              this.emit('thumbnail', file, 'https://cdn-icons-png.flaticon.com/512/337/337946.png');
            if (ext === 'doc' || ext === 'docx')
              this.emit('thumbnail', file, 'https://cdn-icons-png.flaticon.com/512/281/281760.png');
            if (ext === 'xls' || ext === 'xlsx')
              this.emit('thumbnail', file, 'https://cdn-icons-png.flaticon.com/512/888/888879.png');
            if (ext === 'ppt' || ext === 'pptx')
              this.emit('thumbnail', file, 'https://cdn-icons-png.flaticon.com/512/888/888857.png');
          });
        },
      });
      return () => dzInstance.current?.destroy();
    }
  }, []);

  /* ---------- Cargar planeaciones R del docente SIN evidencias + siguiente IdEvidencia ---------- */
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        const idUsuario = localStorage.getItem('idUsuario');
        if (!idUsuario) {
          Swal.fire('Falta sesión', 'No se encontró idUsuario en localStorage', 'warning');
          return;
        }

        // 1) Todas las planeaciones del docente
        const { data: planesData } = await fetchJsonWithFallback(`/planeaciones/usuario/${idUsuario}`);

        // 2) Filtro a R (revisadas)
        const revisadas = Array.isArray(planesData)
          ? planesData.filter(p => String(p.Status || '').toUpperCase() === 'R')
          : [];

        // 3) Para cada R, preguntamos si ya tiene evidencias; nos quedamos SOLO con las que NO tienen
        const checks = await Promise.all(
          revisadas.map(async (p) => {
            try {
              const { data } = await fetchJsonWithFallback(`/evidencias/planeacion/${p.IdPlaneaciones}`);
              const tiene = Array.isArray(data) && data.length > 0;
              return { ...p, tieneEvidencias: !!tiene };
            } catch (err) {
              console.warn('No se pudo verificar evidencias de planeación', p.IdPlaneaciones, err);
              return { ...p, tieneEvidencias: true };
            }
          })
        );

        const disponibles = checks.filter(p => !p.tieneEvidencias);
        setPlaneacionesDisponibles(disponibles);

        // Prefija última disponible (si hay)
        if (disponibles.length) {
          setIdPlaneacion(String(disponibles[disponibles.length - 1].IdPlaneaciones));
        } else {
          setIdPlaneacion('');
        }

        // 4) Siguiente IdEvidencia (solo display)
        const { data: dataNext } = await fetchJsonWithFallback('/evidencias/siguiente-id');
        setSiguienteId(dataNext?.siguienteId ?? '');
      } catch (err) {
        console.error(err);
        Swal.fire('Error', err.message || 'No se pudieron cargar datos iniciales', 'error');
      } finally {
        setCargando(false);
      }
    };
    cargarDatos();
  }, [fetchJsonWithFallback]);

  /* ---------- Confirmación al recargar/cerrar con cambios ---------- */
  useEffect(() => {
    const beforeUnload = (e) => {
      const files = dzInstance.current?.getAcceptedFiles?.() || dzInstance.current?.files || [];
      if (descripcion.trim() || (files.length > 0)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [descripcion]);

  /* ---------- Registrar Evidencia ---------- */
  const handleRegistrar = async (e) => {
    e.preventDefault();
    if (enviando) return;

    // ===== VALIDACIONES DEFENSIVAS =====
    // Dropzone listo
    if (!dzInstance.current || (!dzInstance.current.getAcceptedFiles && !dzInstance.current.files)) {
      Swal.fire('Error', 'El área de subida no está lista. Recarga la página.', 'error');
      return;
    }

    // Conectividad
    if (!navigator.onLine) {
      Swal.fire('Sin conexión', 'Revisa tu conexión a internet.', 'error');
      return;
    }

    // Planeación válida aún en la lista
    if (!idPlaneacion || !planeacionesDisponibles.some(p => String(p.IdPlaneaciones) === String(idPlaneacion))) {
      Swal.fire('Error', 'La planeación seleccionada ya no está disponible.', 'error');
      return;
    }

    // Descripción (opcional) → tope BD 254
    if (descripcion && descripcion.length > 254) {
      Swal.fire('Descripción muy larga', 'Máximo 254 caracteres.', 'info');
      return;
    }

    // Archivos: preferir los aceptados por Dropzone
    const files = (dzInstance.current.getAcceptedFiles?.() || dzInstance.current.files || []);
    if (!files.length) {
      Swal.fire('Error', 'Debes adjuntar al menos un archivo', 'error');
      return;
    }

    // ***** Regla solicitada: mínimo 500 imágenes *****
    const MIN_IMAGES = 1; // <-- puedes ajustar este mínimo aquí
    const imageExt = ['jpg','jpeg','png'];
    const imageCount = files.filter(f => imageExt.includes((f.name.split('.').pop()||'').toLowerCase())).length;
    if (imageCount < MIN_IMAGES) {
      Swal.fire('Faltan imágenes', `Debes adjuntar al menos ${MIN_IMAGES} imágenes (JPG/PNG). Actualmente: ${imageCount}.`, 'warning');
      return;
    }

    // Número máximo de archivos (opcional; déjalo alto si vas a subir 500+)
    const MAX_FILES = 2000;
    if (files.length > MAX_FILES) {
      Swal.fire('Demasiados archivos', `Máximo ${MAX_FILES} archivos por envío.`, 'info');
      return;
    }

    // Extensiones permitidas (cobertura adicional)
    const ALLOWED = ['jpg','jpeg','png','pdf','doc','docx','xls','xlsx','ppt','pptx'];
    const badExt = files.filter(f => !ALLOWED.includes((f.name.split('.').pop()||'').toLowerCase()));
    if (badExt.length) {
      Swal.fire('Formato no permitido', `No permitido: ${badExt.map(f=>f.name).join(', ')}`, 'error');
      return;
    }

    // Duplicados por nombre
    const names = files.map(f => f.name.toLowerCase());
    const dup = names.filter((n,i)=> names.indexOf(n)!==i);
    if (dup.length) {
      Swal.fire('Duplicados', `Nombres repetidos: ${[...new Set(dup)].join(', ')}`, 'warning');
      return;
    }

    // Caracteres problemáticos en nombres
    const forbiddenPattern = /[<>:"/\\|?*]/;
    const hasControlChars = (name = '') => {
      for (let i = 0; i < name.length; i += 1) {
        if (name.charCodeAt(i) < 32) return true;
      }
      return false;
    };
    const badNames = files.filter((f) => forbiddenPattern.test(f.name) || hasControlChars(f.name));
    if (badNames.length) {
      Swal.fire('Nombre inválido', `Evita caracteres especiales en: ${badNames.map(b=>b.name).join(', ')}`, 'error');
      return;
    }

    // Tamaño por archivo (refuerzo 10 MB)
    const MAX_PER_MB = 10;
    const tooBig = files.filter(f => (f.size || 0)/1024/1024 > MAX_PER_MB);
    if (tooBig.length) {
      Swal.fire('Archivo muy grande', `Máx ${MAX_PER_MB} MB por archivo. Revisa: ${tooBig.map(f=>f.name).join(', ')}`, 'warning');
      return;
    }

    // Tamaño total (lo dejo MUY alto para permitir 500 imágenes; ajusta si tu backend lo requiere)
    const MAX_TOTAL_MB = 200000; // ~200 GB
    const totalMB = files.reduce((s,f)=> s + (f.size||0)/1024/1024, 0);
    if (totalMB > MAX_TOTAL_MB) {
      Swal.fire('Límite total superado', `El total (${totalMB.toFixed(1)} MB) excede ${MAX_TOTAL_MB} MB.`, 'warning');
      return;
    }

    try {
      setEnviando(true);

      // Rechequeo de carrera (por si ya registraron evidencias en paralelo)
      try {
        const { data: dataCheck } = await fetchJsonWithFallback(`/evidencias/planeacion/${idPlaneacion}`);
        if (Array.isArray(dataCheck) && dataCheck.length > 0) {
          Swal.fire('Aviso', 'Esta planeación ya tiene evidencias registradas.', 'warning');
          // Saca esta planeación del combo
          setPlaneacionesDisponibles(prev => prev.filter(p => String(p.IdPlaneaciones) !== String(idPlaneacion)));
          setIdPlaneacion('');
          return;
        }
      } catch (err) {
        console.warn('Fallo el recheck de evidencias antes de registrar', err);
        Swal.fire('Error', 'No se pudo verificar la planeación seleccionada. Intenta de nuevo.', 'error');
        return;
      }

      const formData = new FormData();
      formData.append('idPlaneaciones', idPlaneacion);
      formData.append('descripcion', sanitize254(descripcion)); // tope BD 254 + sanitización

      // Enviar idUsuario
      const idUsuario = localStorage.getItem('idUsuario');
      if (idUsuario) formData.append('idUsuario', idUsuario);

      // Archivos de Dropzone
      files.forEach((file) => {
        formData.append('archivos', file);
      });

      const { data } = await fetchJsonWithFallback('/evidencias/registrar', {
        method: 'POST',
        body: formData,
      });
      if (data?.ok === false) throw new Error(data?.error || 'Error desconocido');

      Swal.fire('Éxito', data.mensaje || 'Evidencia registrada', 'success');

      // Limpiar y sacar esa planeación del combo (ya no debe permitir otro registro)
      setDescripcion('');
      dzInstance.current?.removeAllFiles();
      setPlaneacionesDisponibles(prev => prev.filter(p => String(p.IdPlaneaciones) !== String(idPlaneacion)));
      setIdPlaneacion('');

      // Recalcular siguiente id
      try {
        const { data: dataNext } = await fetchJsonWithFallback('/evidencias/siguiente-id');
        setSiguienteId(dataNext?.siguienteId ?? '');
      } catch (err) {
        console.warn('No se pudo refrescar el siguiente ID', err);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.message, 'error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
        <small className="text-muted fs-5">Registrar Evidencias</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3" onSubmit={handleRegistrar}>
          <div className="col-md-4">
            <label htmlFor="idEvidencia" className="form-label">ID Evidencia (siguiente)</label>
            <input
              type="text"
              className="form-control"
              id="idEvidencia"
              value={siguienteId}
              readOnly
            />
          </div>

          <div className="col-md-8">
            <label htmlFor="descripcion" className="form-label">Descripción</label>
            <input
              type="text"
              className="form-control"
              id="descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Breve descripción de la evidencia (opcional)"
              maxLength={254} // tope BD
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="temaPlaneacion" className="form-label">
              Planeación revisada (R) sin evidencias
            </label>
            <select
              id="temaPlaneacion"
              className="form-select"
              value={idPlaneacion}
              onChange={(e) => setIdPlaneacion(e.target.value)}
              disabled={cargando || planeacionesDisponibles.length === 0}
            >
              <option value="">
                {cargando ? 'Cargando...' : 'Seleccionar'}
              </option>
              {planeacionesDisponibles.map((p) => (
                <option key={p.IdPlaneaciones} value={p.IdPlaneaciones}>
                  {p.Tema || `Planeación #${p.IdPlaneaciones}`} — {p.Status}
                </option>
              ))}
            </select>
            {!cargando && planeacionesDisponibles.length === 0 && (
              <small className="text-danger">
                No hay planeaciones R disponibles (ya tienen evidencias o no existen).
              </small>
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Subir Evidencias (imágenes/archivos)</label>
            <div id="dropzoneEvidencias" className="dropzone" ref={dropzoneRef}></div>
            <div className="form-text mt-1">
              * Requisito: al menos 500 imágenes (JPG/PNG). Tamaño máx por archivo: 10 MB.
            </div>
          </div>

          {/* Botones */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button type="submit" className="btn btn-success px-4" disabled={enviando}>
              {enviando ? 'Registrando...' : 'Registrar'}
            </button>
            <button type="button" className="btn btn-danger px-4" onClick={handleSalir} disabled={enviando}>
              Salir
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarEvidencias;
